// The one data path for cards and panes (docs/005 §5a). The desktop answers
// from its local store (Tauri invoke); the web asks the user's desktop through
// the relay. Components never import a platform API directly.
import { createContext, useContext, type ReactNode } from "react";

export type DeploymentAction = "pause" | "resume" | "stop";
export type AlertAction = "pause" | "resume";

/** What a surface can do; a pane hides a control whose capability is off. */
export type Capabilities = {
  /** Pause / resume / stop a deployment from the pane. */
  controlDeployments: boolean;
  /** Show local file paths (run folders) — meaningless on the web. */
  localPaths: boolean;
  /** Pause / resume alerts from the pane. */
  controlAlerts: boolean;
};

export type RecipientStatus = "pending" | "confirmed" | "unsubscribed" | "bounced";
export type Recipient = { id: number; email: string; status: RecipientStatus; can_resend?: boolean };
/** An alert's extra email recipients (Stock-Screener doc 288). Prod owns them and sends the
 * one confirmation email; desktop proxies to prod, web calls prod directly. `list` answers
 * synced=false while the alert hasn't reached prod yet; unavailable=true when the alert is on prod
 * but prod has no recipients route. Mutations reject with prod's message. */
export interface RecipientsApi {
  list(alertId: string): Promise<{ synced: boolean; unavailable?: boolean; recipients: Recipient[]; count?: number; cap?: number }>;
  add(alertId: string, email: string): Promise<void>;
  remove(alertId: string, recipientId: number): Promise<void>;
  resend(alertId: string, recipientId: number): Promise<void>;
}

export interface DataClient {
  backtestRun(runId: string): Promise<any>;
  deployment(deploymentId: string): Promise<any>;
  setDeployment(deploymentId: string, action: DeploymentAction): Promise<void>;
  /** Calls `cb` when this deployment's data may have changed (desktop: its
   * scheduler ran; web: a periodic refresh). Returns an unsubscribe. */
  onDeploymentUpdate(deploymentId: string, cb: () => void): () => void;
  /** Alerts and watchlists (made by the agent; the panes only show and pause them). */
  alertsOverview(): Promise<any>;
  alert(alertId: string): Promise<any>;
  setAlert(alertId: string, action: AlertAction): Promise<void>;
  watchlist(watchlistId: string): Promise<any>;
  /** Calls `cb` when alerts may have changed (a run, a sync). Returns an unsubscribe. */
  onAlertsUpdate(cb: () => void): () => void;
  /** Alert email recipients; absent = the surface can't show them. */
  recipients?: RecipientsApi;
  capabilities: Capabilities;
}

const Ctx = createContext<DataClient | null>(null);

export function DataClientProvider({ client, children }: { client: DataClient; children: ReactNode }) {
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

export function useDataClient(): DataClient {
  const c = useContext(Ctx);
  if (!c) throw new Error("shared-ui: wrap the app in <DataClientProvider>");
  return c;
}
